import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { useQuery } from '@tanstack/react-query'
import { apiFetch, hasLiveApi } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  fromDownlineAccountsData,
  fromFixturePnl,
  unavailablePlStatement,
  plStatementRangeError,
  type GetDownlineAccountsDataResponse,
  type PlStatementQuery,
  type PlStatementResult,
} from '@/api/map/plStatement'
import * as api from '@/data/api'
import { FIXTURE_GAMES } from '@/api/map/game'
import { keys } from '@/api/hooks/shared/keys'
import { getWorld } from '@/data/world'
import type { GameBucket } from '@/data/types'
import type { GetDownlineTotalNetAmountResponse } from '@/features/member/memberStatement'

export type MemberStatementQuery = PlStatementQuery & {
  /** Lab ForMember / TotalNetAmount body — viewed seat uuid. */
  memberId: string
}

/**
 * Body for Lab `getDownlineAccountsForMember` (+ New) / TotalNetAmount.
 * Agent-API picks source like Lab PLStatementCtrl: today → PG (Mongo), else → BQ (≤93d).
 */
export function forMemberBody(query: MemberStatementQuery, page: number, pageSize: number) {
  const body: Record<string, unknown> = {
    memberId: query.memberId,
    fromDate: query.range.from,
    toDate: query.range.to,
    page,
    pageSize,
  }
  if (query.game && query.game !== 'all') body.game = query.game
  if (query.subGame && query.subGame !== 'All') body.subGame = query.subGame
  return body
}

export async function loadMemberStatement(query: MemberStatementQuery): Promise<PlStatementResult> {
  const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE
  const page = query.page ?? 1
  const range = {
    from: query.range.from.slice(0, 10),
    to: query.range.to.slice(0, 10),
  }
  const rangeErr = plStatementRangeError(range)
  if (rangeErr) {
    return unavailablePlStatement({ ...query, page, pageSize, range }, rangeErr)
  }

  if (hasLiveApi()) {
    try {
      const live = await apiFetch<GetDownlineAccountsDataResponse>(
        '/v1/account/getDownlineAccountsForMember',
        { method: 'POST', body: JSON.stringify(forMemberBody(query, page, pageSize)) },
      )
      return fromDownlineAccountsData(live, { ...query, page, pageSize, range })
    } catch (err) {
      if (isUnreachableApiError(err)) {
        return unavailablePlStatement(
          { ...query, page, pageSize, range },
          'Event-level Account Statement needs Agent-API hierarchy POST /v1/account/getDownlineAccountsForMember. It is not reachable, so this console will not invent settled rows.',
        )
      }
      const message = err instanceof Error ? err.message : 'Request failed'
      return unavailablePlStatement({ ...query, page, pageSize, range }, message)
    }
  }

  const catalogName = FIXTURE_GAMES.find((row) => row._id === query.game)?.name
  const catalogGame =
    catalogName === 'SportsBook' ||
    catalogName === 'Live Casino' ||
    catalogName === 'Card Game'
      ? (catalogName as GameBucket)
      : undefined

  const summary = await api.getPnl({
    ...query,
    range,
    page: 1,
    pageSize: 100_000,
    memberIds: [query.memberId],
    ...(catalogGame ? { games: [catalogGame] } : {}),
  })
  const world = getWorld()
  const memberName = (memberId: string) => world.byId.get(memberId)?.username ?? memberId
  return fromFixturePnl(summary, { ...query, page, pageSize, range }, memberName)
}

export async function loadMemberTotalNetAmount(
  query: MemberStatementQuery,
): Promise<GetDownlineTotalNetAmountResponse | null> {
  if (!hasLiveApi()) return null
  const range = {
    from: query.range.from.slice(0, 10),
    to: query.range.to.slice(0, 10),
  }
  if (plStatementRangeError(range)) return null
  try {
    const body = forMemberBody(query, 1, 1)
    delete body.page
    delete body.pageSize
    return await apiFetch<GetDownlineTotalNetAmountResponse>(
      '/v1/account/getDownlineTotalNetAmount',
      { method: 'POST', body: JSON.stringify(body) },
    )
  } catch {
    return null
  }
}

export function useMemberStatement(query: MemberStatementQuery) {
  return useQuery({
    queryKey: keys.memberStatement(query),
    queryFn: () => loadMemberStatement(query),
    enabled: Boolean(query.memberId),
    placeholderData: (previous) => previous,
  })
}

export function useMemberTotalNetAmount(query: MemberStatementQuery) {
  const sumKey = {
    memberId: query.memberId,
    from: query.range.from,
    to: query.range.to,
    ...(query.game && query.game !== 'all' ? { game: query.game } : {}),
    ...(query.subGame ? { subGame: query.subGame } : {}),
  }
  return useQuery({
    queryKey: keys.memberTotalNetAmount(sumKey),
    queryFn: () => loadMemberTotalNetAmount(query),
    enabled: hasLiveApi() && Boolean(query.memberId),
    placeholderData: (previous) => previous,
  })
}
